import { Injectable } from '@angular/core';
import { formatCurrency, formatDate } from '@angular/common';
import { combineLatest, map, ReplaySubject, tap } from 'rxjs';

import {
  BenefitsDetail,
  Check,
  DetailRows,
  // NetIncome,
  PaymentInfo,
  PaystubData,
  PaystubDetailItem,
  PaystubDetailsView,
  PaystubRows,
  PaystubsCollection,
  PaystubInfo,
  TotalRows,
} from '@core/models';
import { AdjustmentType } from '@core/enums';
import { NetIncomeService, PaystubsCollectionService, PaystubDetailsService } from '@core/services';
import { piechartOptions } from '@paystubs/constants/highcharts-piechart';
@Injectable()
export class PaystubsService {
  private tabsSubject = new ReplaySubject<string>(1);

  tabsTitle$ = this.tabsSubject.asObservable();

  //* Observables
  private checks$ = this.paystubsCollectionService.checks$.pipe(
    map(paystubInfo => this.buildChecks(paystubInfo))
  );

  private paystubsDetails$ = this.paystubDetailsService.paystubDetailsData$.pipe(
    map(paystubDetail => this.buildPaystubDetail(paystubDetail))
  );

  paystubs$ = combineLatest([this.checks$, this.paystubsDetails$]).pipe(
    map(([checks, details]) => ({ checks, details }))
  );

  pieChartOptions$ = this.paystubDetailsService.paystubDetailsData$.pipe(
    map(checks => this.buildPieChartOptions(checks))
  );

  constructor(
    private paystubDetailsService: PaystubDetailsService,
    private paystubsCollectionService: PaystubsCollectionService
  ) {}

  private buildDetailRows(paystubDetail: PaystubData): PaystubRows {
    if (paystubDetail) {
      const detailRows: DetailRows = {
        paymentRows: [],
        summaryRows: [],
        preTaxRows: [],
        taxRows: [],
        postTaxRows: [],
        earningRows: [],
        netPayRows: [],
        timeOffRows: [],
      };

      const totalRows: TotalRows = {
        earningTotalRow: [],
        postTaxTotalRow: [],
        preTaxTotalRow: [],
        taxTotalRow: [],
      };

      const summary = paystubDetail.paystubSummaryCollection;

      totalRows.preTaxTotalRow = [
        'Total',
        formatCurrency(summary.preTaxDeductionsTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdPreTaxDeductions, 'en-US', '$', '1.2-2'),
      ];
      totalRows.postTaxTotalRow = [
        'Total',
        formatCurrency(summary.postTaxDeductionsTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdPostTaxDeductions, 'en-US', '$', '1.2-2'),
      ];
      totalRows.earningTotalRow = [
        'Total',
        formatCurrency(summary.earningsHoursTotal, 'en-US', '', '1.2-2'),
        '',
        formatCurrency(summary.earningsTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdEarningsTotal, 'en-US', '$', '1.2-2'),
      ];
      totalRows.taxTotalRow = [
        'Total',
        formatCurrency(summary.taxesTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdTaxesTotal, 'en-US', '$', '1.2-2'),
      ];

      detailRows.summaryRows.push([
        'Take Home Pay',
        formatCurrency(summary.netPay, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdNetPay, 'en-US', '$', '1.2-2'),
      ]);

      detailRows.summaryRows.push([
        'Pre Tax Deductions',
        formatCurrency(summary.preTaxDeductionsTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdPreTaxDeductions, 'en-US', '$', '1.2-2'),
      ]);

      detailRows.summaryRows.push([
        'Taxes',
        formatCurrency(summary.taxesTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdTaxesTotal, 'en-US', '$', '1.2-2'),
      ]);
      detailRows.summaryRows.push([
        'Post Tax Deductions',
        formatCurrency(summary.postTaxDeductionsTotal, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdPostTaxDeductions, 'en-US', '$', '1.2-2'),
      ]);

      detailRows.netPayRows.push([
        'Net Pay',
        formatCurrency(summary.netPay, 'en-US', '$', '1.2-2'),
        formatCurrency(summary.ytdNetPay, 'en-US', '$', '1.2-2'),
      ]);

      const earningItems =
        paystubDetail.paystubDetailCollection.earningsDetail == null
          ? []
          : paystubDetail.paystubDetailCollection.earningsDetail;
      earningItems.forEach((earningItem: PaystubDetailItem) => {
        detailRows.earningRows.push([
          earningItem.itemName,
          earningItem.hour > 0 ? formatCurrency(earningItem.hour, 'en-US', '', '1.2-2') : '',
          earningItem.rate > 0 ? formatCurrency(earningItem.rate, 'en-US', '$', '1.2-2') : '',
          formatCurrency(earningItem.currentAmount, 'en-US', '$', '1.2-2'),
          formatCurrency(earningItem.ytdAmount, 'en-US', '$', '1.2-2'),
        ]);
      });

      const paymentDetails =
        paystubDetail.paystubDetailCollection.paymentDetails == null
          ? []
          : paystubDetail.paystubDetailCollection.paymentDetails;
      paymentDetails.forEach((payment: PaymentInfo) =>
        detailRows.paymentRows.push([
          payment.transactionType,
          payment.bankAccountNumber,
          formatCurrency(payment.amount, 'en-US', '$', '1.2-2'),
        ])
      );

      const preTaxItems =
        paystubDetail.paystubDetailCollection.preTaxDeductionsDetail == null
          ? []
          : paystubDetail.paystubDetailCollection.preTaxDeductionsDetail;
      preTaxItems.forEach((preTaxItem: PaystubDetailItem) => {
        detailRows.preTaxRows.push([
          preTaxItem.itemName,
          formatCurrency(preTaxItem.currentAmount, 'en-US', '$', '1.2-2'),
          formatCurrency(preTaxItem.ytdAmount, 'en-US', '$', '1.2-2'),
        ]);
      });

      const taxItems =
        paystubDetail.paystubDetailCollection.taxesDetail == null
          ? []
          : paystubDetail.paystubDetailCollection.taxesDetail;
      taxItems.forEach((taxItem: PaystubDetailItem) => {
        detailRows.taxRows.push([
          taxItem.itemName,
          formatCurrency(taxItem.currentAmount, 'en-US', '$', '1.2-2'),
          formatCurrency(taxItem.ytdAmount, 'en-US', '$', '1.2-2'),
        ]);
      });

      const postTaxItems =
        paystubDetail.paystubDetailCollection.postTaxDeductionsDetail == null
          ? []
          : paystubDetail.paystubDetailCollection.postTaxDeductionsDetail;
      postTaxItems.forEach((postTaxItem: PaystubDetailItem) => {
        detailRows.postTaxRows.push([
          postTaxItem.itemName,
          formatCurrency(postTaxItem.currentAmount, 'en-US', '$', '1.2-2'),
          formatCurrency(postTaxItem.ytdAmount, 'en-US', '$', '1.2-2'),
        ]);
      });

      const timeOffItems =
        paystubDetail.paystubDetailCollection.benefitsDetail == null
          ? []
          : paystubDetail.paystubDetailCollection.benefitsDetail;
      timeOffItems.forEach((timeOffItem: BenefitsDetail) => {
        detailRows.timeOffRows.push([
          timeOffItem.benefitName,
          timeOffItem.usedHours.toString(),
          timeOffItem.earnedHours,
          timeOffItem.availableHours,
        ]);
      });
      return { detailRows: detailRows, totalRows: totalRows };
    } else return null;
  }

  private buildPaystubDetail(paystubDetail?: PaystubData): PaystubDetailsView {
    if (paystubDetail) {
      const summary = paystubDetail.paystubSummaryCollection;

      const paystubRows: PaystubRows = this.buildDetailRows(paystubDetail);
      const detailRows = paystubRows.detailRows;
      const totalRows = paystubRows.totalRows;

      return {
        checkDate: formatDate(summary.checkDate, 'shortDate', 'en-US'),
        periodStartDate: formatDate(summary.periodBeginDate, 'shortDate', 'en-US'),
        periodEndDate: formatDate(summary.periodEndDate, 'shortDate', 'en-US'),
        adjustmentType: summary.adjustmentType,
        paymentMethod: {
          color: '',
          icon: '',
          title: 'Payment Method',
          headerCells: ['', 'Account', 'Amount'],
          dataRows: detailRows.paymentRows,
          colspan: 3,
        },
        summary: {
          color: '',
          icon: '',
          title: 'Summary',
          headerCells: ['', 'Current', 'Year to Date'],
          dataRows: detailRows.summaryRows,
          colspan: 3,
        },
        timeOff: {
          color: '',
          icon: '',
          title: 'Time Off',
          headerCells: ['', 'Used', 'Earned', 'Available'],
          dataRows: detailRows.timeOffRows,
          colspan: 4,
        },
        rightTableCollection: [
          {
            color: '',
            icon: '',
            title: 'Earnings',
            headerCells: ['', 'Hours', 'Rate', 'Current', 'Year to Date'],
            dataRows: detailRows.earningRows,
            totalRow: totalRows.earningTotalRow,
            colspan: 5,
          },
          {
            color: '#FE5A58',
            icon: 'circle circle-orange',
            title: 'Pre-Tax Deductions',
            headerCells: ['', 'Current', 'Year to Date'],
            dataRows: detailRows.preTaxRows,
            totalRow: totalRows.preTaxTotalRow,
            colspan: 3,
          },
          {
            color: '#5D62F6',
            icon: 'circle circle-blue',
            title: 'Taxes',
            headerCells: ['', 'Current', 'Year to Date'],
            dataRows: detailRows.taxRows,
            totalRow: totalRows.taxTotalRow,
            colspan: 3,
          },
          {
            color: '#FFEFC5',
            icon: 'circle circle-yellow',
            title: 'Post-Tax Deductions',
            headerCells: ['', 'Current', 'Year to Date'],
            dataRows: detailRows.postTaxRows,
            totalRow: totalRows.postTaxTotalRow,
            colspan: 3,
          },
          {
            color: '#01D788',
            icon: 'circle circle-green',
            title: 'Take Home Pay',
            headerCells: ['', 'Current', 'Year to Date'],
            dataRows: detailRows.netPayRows,
            colspan: 3,
          },
        ],
      };
    } else return null;
  }

  private buildPieChartOptions(paystubDetail?: PaystubData): Highcharts.Options {
    if (paystubDetail) {
      const summary = paystubDetail?.paystubSummaryCollection;
      if (!summary) return;

      const data = [
        { name: 'Take Home Pay', y: summary.netPay },
        { name: 'Post Tax Deductions', y: summary.postTaxDeductionsTotal },
        { name: 'Pre Tax Deductions', y: summary.preTaxDeductionsTotal },
        { name: 'Taxes', y: summary.taxesTotal },
      ];
      //* Chart Options
      const series = [{ type: 'pie', innerSize: '50%', data }];
      return Object.assign({}, piechartOptions, { series });
    } else return null;
  }

  private buildChecks(checks: Check[]): PaystubsCollection[] {
    if (checks.length) {
      //* Grab all unique CheckYears in descending order
      const uniqueCheckYears = [...new Set(checks.map(x => x.checkYear))];

      const response: PaystubsCollection[] = [];

      for (let i = 0; i < uniqueCheckYears.length; i++) {
        // We want to order paystubs by years.
        // Grab the 1st unique year for which we have paystubs for this employee
        const year = uniqueCheckYears[i];

        // Filter the paystubs collection to only ones that are for the year from above
        // For each filtered paystub , map & create a new Object of type PaystubInfo
        const paystubInfo = checks
          .filter(paystubs => {
            return paystubs.checkYear === year;
          })
          .map(paystub => {
            const paystubDataForTab: PaystubInfo = {
              day: formatDate(paystub.checkDate, 'mediumDate', 'en-US'),
              amount: formatCurrency(paystub.netPay, 'en-US', '$', '1.2-2'),
              checkId: paystub.checkId,
              didId: paystub.didId,
              isVoided: paystub.adjustmentType.toUpperCase().trim() === AdjustmentType.Void,
              isActive: false,
            };
            return paystubDataForTab;
          });
        this.paystubDetailsService.getPaystubDetail(paystubInfo[0]);

        response.push({
          title: `${year}`,
          payInfo: [...paystubInfo],
        });
      }
      return response;
    } else {
      this.paystubDetailsService.getPaystubDetail(null);
      return [];
    }
  }

  addTabTitle(newTitle: string): void {
    this.tabsSubject.next(newTitle);
  }
}
